import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/index.css";
import App from "@/App";
import CatalogPage from "@/components/catalog/CatalogPage";
import VehicleDetailPage from "@/components/catalog/VehicleDetailPage";
import LoginPage from "@/components/LoginPage";
import { BusinessProvider } from "@/contexts/BusinessContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Detección de dominio para servir el catálogo público directamente en la raíz
 * de subdominios como `autos.hebeh.com.mx` (sin el prefijo /catalogo/automotriz).
 * Mapeo: hostname → businessSlug.
 */
const CATALOG_DOMAINS = {
  "automotriz.hebeh.com.mx": "automotriz",
  // Fácil agregar más en el futuro: p.ej. "maquinaria.hebeh.com.mx": "maquinaria"
};

function getCatalogSlugForHostname() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  if (CATALOG_DOMAINS[host]) return CATALOG_DOMAINS[host];
  // Match por prefijo (`automotriz.*`, `automotriz-preview.*`) — útil para preview/staging
  for (const domain of Object.keys(CATALOG_DOMAINS)) {
    const sub = domain.split(".")[0]; // "automotriz"
    if (host === sub || host.startsWith(sub + ".") || host.startsWith(sub + "-")) {
      return CATALOG_DOMAINS[domain];
    }
  }
  return null;
}

function ProtectedApp() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0B1623] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <App />;
}

const CATALOG_SLUG = getCatalogSlugForHostname();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AuthProvider>
      <BusinessProvider>
        <BrowserRouter>
          <Routes>
            {CATALOG_SLUG ? (
              // Modo "subdominio del catálogo" (ej. autos.hebeh.com.mx).
              // Toda la app sirve el catálogo público — el admin no es accesible por este dominio.
              // El path raíz "/" se sirve directamente sin redirect para que la URL quede limpia.
              <>
                <Route path="/" element={<CatalogPage />} />
                <Route path="/v/:vehicleId" element={<VehicleDetailPage />} />
                <Route path="/catalogo" element={<CatalogPage />} />
                <Route path="/catalogo/:businessSlug" element={<CatalogPage />} />
                <Route path="/catalogo/:businessSlug/:vehicleId" element={<VehicleDetailPage />} />
                <Route path="/catalogo/v/:vehicleId" element={<VehicleDetailPage />} />
                {/* URL SEO-friendly: automotriz.hebeh.com.mx/nissan/versa */}
                <Route path="/:brandSlug/:modelSlug" element={<VehicleDetailPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              // Modo "app administrativa" (ej. sistema.hebeh.com.mx).
              <>
                <Route path="/catalogo" element={<CatalogPage />} />
                <Route path="/catalogo/:businessSlug" element={<CatalogPage />} />
                <Route path="/catalogo/:businessSlug/:vehicleId" element={<VehicleDetailPage />} />
                <Route path="/catalogo/v/:vehicleId" element={<VehicleDetailPage />} />
                <Route path="/*" element={<ProtectedApp />} />
              </>
            )}
          </Routes>
        </BrowserRouter>
      </BusinessProvider>
    </AuthProvider>
  </React.StrictMode>,
);
